import type {ReactElement} from 'react';
import {useRef} from 'react';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {SocialPostContent} from '@Components/social-media/components/social-post-content-multiposting';
import {SocialPostContentType} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import {getMobilePreviewWindowHeight} from '@Components/social-media-wizard/social-media-wizard-library';
import type {WizardPreviewPanelProps} from './wizard-preview-panel.types';
import styles from '../../social-media-wizard.module.scss';

export function WizardPreviewPanel({
  wizardStep,
  designsToPublishTo,
  postPublishingParams,
  designsGraphicsMetaMap,
  isSelectTemplateFlow,
  isMobileView,
  isPreviewLoading,
  areAllPreviewsUpToDate,
  ctaSlot,
  onOrphanedPreviewClick,
  onPreviewLoaded,
}: WizardPreviewPanelProps): ReactElement<any> {
  const previewParentRef = useRef<HTMLDivElement>(null);

  const onOrphanedPreviewContainerClick = (): void => {
    if (isSelectTemplateFlow || isPreviewLoading) {
      return;
    }

    onOrphanedPreviewClick();
  };

  const onLoadCallback = (): void => {
    onPreviewLoaded();
  };

  const getPreviewStyles = (): NonNullable<unknown> | undefined => {
    if (!isMobileView) {
      return undefined;
    }

    return {
      height: `${getMobilePreviewWindowHeight()}px`,
    };
  };

  const getPreviewContentWrapperClasses = (): string => {
    return `${styles.previewWrapper} ${ctaSlot === null ? styles.anonTemplateWrapper : ''} ${wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS ? styles.selectAccountsStepWrapper : ''} `;
  };

  const getSocialPostContent = (): ReactElement<any> | null => {
    if (wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS && !areAllPreviewsUpToDate) {
      return null;
    }

    return (
      <SocialPostContent
        designsToPublish={designsToPublishTo}
        onLoadCallback={onLoadCallback}
        areDetailsLoading={false}
        parentContainerRef={previewParentRef}
        type={SocialPostContentType.ORPHANED_PREVIEW}
        onOrphanedPreviewContainerClick={onOrphanedPreviewContainerClick}
        accountsToPublishTo={[]}
        publishingParams={postPublishingParams}
        designsGraphicMetaMap={designsGraphicsMetaMap}
      />
    );
  };

  const isLoading = wizardStep === SocialMediaWizardStep.SELECT_ACCOUNTS ? isPreviewLoading && !areAllPreviewsUpToDate : isPreviewLoading;

  return (
    <div ref={previewParentRef} style={getPreviewStyles()} className={getPreviewContentWrapperClasses()}>
      <CircularProgressLoader classes={isLoading ? '_position-absolute' : '_hidden'} size={LOADER_SIZE.MEDIUM} />
      <div className={`_full-height _full-width ${isLoading ? '_invisible' : ''}`}>{getSocialPostContent()}</div>
      {ctaSlot}
    </div>
  );
}
